import {
  useCallback,
  useContext, useEffect, useState
} from 'react'
import { TrackType } from '@prisma/client'
import Link from 'next/link'
import Modal from 'react-modal'

import { RestrictionContext } from '@src/context/RestrictionContext'
import { useMultilingualCaptions } from '@src/context/LanguageContext'
import { CaptionsContext } from '@src/context/CaptionsContext'

import { Settings, ArrowLeft } from '@src/components/icons'
import SettingsOverlay from '@src/components/SettingsOverlay'

import TranscriptPresentation from './TranscriptPresentation'
import LiveCaptionsPresentation from './LiveCaptionsPresentation'
import { getStatus, PlayerStatus } from '../lib/monitoring'
import useToggle from '../lib/useToggle'
import { WorkWithParsedSrts } from '../pages/api/work/[id]'


type FullscreenCaptionsProps = {
  work: WorkWithParsedSrts;
}

export const FullscreenCaptions: React.FC<FullscreenCaptionsProps> = (
  { work }: FullscreenCaptionsProps
) => {
  const [
    showSettingsOverlay,
    toggleSettingsOverlay,
  ] = useToggle(false)
  const { captionMode, setCaptionMode } = useContext(CaptionsContext)
  const multilingualCaptions = useMultilingualCaptions(work)

  const [isOutOfSync, setIsOutOfSync] = useState(false)
  const {
    restricted, isLoading: isLoadingRestricted, showGeoInstructions, setGeofencingUserApproved, netmaskMessage,
  } = useContext(RestrictionContext)

  const { duration } = work
  const lastSyncTimestamp: bigint = work.lastTimestamp

  const allowGeofencing = useCallback(() => {
    setGeofencingUserApproved(true)
  }, [setGeofencingUserApproved])

  useEffect(() => {
    const syncStatusNotOk = (
      getStatus(lastSyncTimestamp, duration) !== PlayerStatus.OK
      && work.loopingWork
    )
    setIsOutOfSync(syncStatusNotOk)
    if (syncStatusNotOk && captionMode !== 'transcript') {
      setCaptionMode('transcript')
    }
  }, [setCaptionMode, captionMode, isOutOfSync, lastSyncTimestamp, duration, work?.loopingWork])

  // useEffect(() => {
  //   console.log('useEffect', work.parsedSrts, captionsPreference)
  //   if (!work.parsedSrts || (work.parsedSrts || []).find((srt) => srt.shortDescriptor === captionsPreference)
  //   ) {
  //     return
  //   }
  //   setShowSettingsOverlay(true)
  // }, [work, setShowSettingsOverlay, captionsPreference])

  const renderedSRT = captionMode === 'transcript'
    ? <TranscriptPresentation work={work} />
    : <LiveCaptionsPresentation />

  const hasAudio = work.tracks?.length || work.audioUrl
  let noCaptionsMessage = <p>No captions for this work.</p>

  if (hasAudio) {
    noCaptionsMessage = work.tracks[0]?.trackType === TrackType.AUDIO_DESCRIPTION
      ? <p>Audio description available. Enable below.</p>
      : <p>Audio available. Enable below.</p>
  }

  const captionDisplay = work.srts?.length > 0
    ? renderedSRT
    : noCaptionsMessage

  const restrictedDisplay = (
    <div className="location-restriction">
      <p className="captions-text">
        You must be at the museum to access this page.
        {netmaskMessage ? ` ${netmaskMessage}` : ''}
        {showGeoInstructions ? '  Please press the "verify location" button to confirm access.' : ''}
      </p>
      {showGeoInstructions && (
        <button type="button" className="geolocate-approval-button" aria-label="Verify location to access content" onClick={allowGeofencing}>Verify Location</button>
      )}
    </div>
  )

  const display = (restricted || isLoadingRestricted) ? restrictedDisplay : captionDisplay
  const determineBacklink = ({ backlinkUrl, exhibitionId }: WorkWithParsedSrts) => {
    if (backlinkUrl) {
      return backlinkUrl
    }
    if (exhibitionId) {
      return `/exhibition/${exhibitionId}`
    }
    return '/'
  }

  return (
    <>
      <div className="mini-header">
        <Link href={determineBacklink(work)}>
          <ArrowLeft />
          Back
        </Link>
        <button type="button" aria-label="Open settings menu" onClick={toggleSettingsOverlay}>
          {multilingualCaptions?.shortDescriptor}
          <span className="settings-icon"><Settings /></span>
        </button>
        <Modal
          className="ReactModal--content"
          overlayClassName="ReactModal--overlay"
          isOpen={showSettingsOverlay}
          appElement={document.getElementById('root')}
          onRequestClose={toggleSettingsOverlay}
        >
          <SettingsOverlay
            close={toggleSettingsOverlay}
            work={work}
            isOutOfSync={isOutOfSync}
          />
        </Modal>
      </div>
      {display}
    </>
  )
}
